import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  input,
  model,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ModalController } from '@ionic/angular';
import { Product, ProductsService } from '@ubie/core';

type SelectedProduct = Product & { isSelected: boolean };
type DisplayProduct = SelectedProduct & { stockCount: number };

@Component({
  selector: 'acl-installation-payment',
  standalone: false,
  templateUrl: './installation-payment.component.html',
  styleUrl: './installation-payment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationPaymentComponent implements OnInit {
  private readonly productsService = inject(ProductsService);
  private readonly modalController = inject(ModalController);

  // products that can be bought through online payment via mollie
  readonly productsToBuyWithPaymentProducts = signal<SelectedProduct[]>([]);

  private readonly inventory = toSignal(
    this.productsService.getInstallerInventory(),
    { initialValue: [] as Product[] },
  );

  private readonly stockMap = computed(() => {
    const map = new Map<string, number>();
    for (const item of this.inventory()) {
      map.set(
        item.uid ?? '',
        (map.get(item.uid ?? '') ?? 0) + (item.amount ?? 0),
      );
    }
    return map;
  });

  readonly originId = input<number>(1);

  readonly filteredProducts = computed((): DisplayProduct[] => {
    const map = this.stockMap();
    return this.productsToBuyWithPaymentProducts()
      .filter((p) =>
        this.originId() === 2
          ? p.name.startsWith('products_qcp')
          : p.name.startsWith('products_qc') &&
            !p.name.startsWith('products_qcp'),
      )
      .map((p) => ({ ...p, stockCount: map.get(p.uid ?? '') ?? 0 }));
  });

  readonly selectedStockCount = computed(
    () => this.stockMap().get(this.selectedProduct()?.uid ?? '') ?? 0,
  );

  readonly buyButtonLabel = computed(() =>
    this.selectedProduct()
      ? 'installation_buyAndComplete'
      : 'installation_complete',
  );

  readonly showUnlimited = computed(() => {
    const date = this.installerAccessUntil();
    if (date) {
      const minUnlimitedDate = new Date();
      minUnlimitedDate.setFullYear(minUnlimitedDate.getFullYear() + 100);

      if (minUnlimitedDate < new Date(date)) {
        return true;
      }
      return false;
    }
    return false;
  });

  @Input() set reload(isReload: boolean) {
    if (isReload) {
      this.initComponent();
    }
  }

  // Emit an event when next is clicked (product is null if skipped)
  @Output() readonly licenseStepNext: EventEmitter<Product | null> =
    new EventEmitter<Product | null>();
  @Output() readonly prevClicked = new EventEmitter<void>();
  @Output() readonly stockAssignSelected = new EventEmitter<Product>();

  readonly btnText = input('Continue');
  readonly showPrevButton = input<boolean>(false);
  readonly selectedProduct = model<Product | undefined>(undefined);
  readonly optionsLabel = input<string>('license_options');
  readonly showLicenseStatus = input<boolean>(false);

  readonly installerAccessUntil = input<Date | undefined>(undefined);

  ngOnInit(): void {
    this.initComponent();
  }

  initComponent(): void {
    this.showProductsToBuyWithPaymentProducts();
  }

  /**
   * Get all base products that can be sold by this site
   */
  showProductsToBuyWithPaymentProducts(): void {
    this.productsService.getProductsToBuyWithPaymentProductsBySite().subscribe({
      next: (result) => {
        this.productsToBuyWithPaymentProducts.set(
          result.map((item) => {
            return {
              ...item,
              isSelected: item.uid === this.selectedProduct()?.uid,
            } as SelectedProduct;
          }),
        );
      },
      error: (error) => {
        console.error('could not get products', error);
      },
    });
  }

  /**
   * When a product has been selected; clicking the active card deselects it
   */
  onProductSelected(product: SelectedProduct): void {
    const alreadySelected = this.selectedProduct()?.uid === product.uid;

    this.productsToBuyWithPaymentProducts.update((current) =>
      current.map((item) => ({
        ...item,
        isSelected: !alreadySelected && item.uid === product.uid,
      })),
    );

    this.selectedProduct.set(alreadySelected ? undefined : product);
  }

  submitProduct(): void {
    this.licenseStepNext.emit(this.selectedProduct() ?? null);
  }

  submitFromStock(): void {
    const product = this.selectedProduct();
    if (product) {
      this.stockAssignSelected.emit(product);
    }
  }
}
